import { useCallback, useContext } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
export function useNavigationBlocker() {
    const { navigator } = useContext(UNSAFE_NavigationContext);
    // WHY: stable across renders (navigator is stable) so a consumer's block-registration effect doesn't
    // unregister/re-register every render — that churn drops the block mid-navigation. The v6 Navigator is
    // backed by a v4 history (HistoryRouter in prod, MemoryRouter in tests) that exposes .block().
    return useCallback((prompt)=>{
        // eslint-disable-next-line no-restricted-syntax
        const blockableHistory = navigator;
        return blockableHistory.block(prompt);
    }, [
        navigator
    ]);
}
export function useHistoryLength() {
    const { navigator } = useContext(UNSAFE_NavigationContext);
    // WHY: read the active router history's length (MemoryRouter in tests, the real history in prod) —
    // window.history.length doesn't reflect the MemoryRouter's back-stack in tests.
    // eslint-disable-next-line no-restricted-syntax
    return navigator.length;
}
export function useLivePathname() {
    const { navigator } = useContext(UNSAFE_NavigationContext);
    // WHY: read the navigator's synchronous location, not the async v6 useLocation — an unmount-time guard
    // (e.g. don't-double-pop) must see the URL as it is NOW, not a stale render snapshot.
    return useCallback(// eslint-disable-next-line no-restricted-syntax
    ()=>navigator.location.pathname, [
        navigator
    ]);
}
export function useLiveSearch() {
    const { navigator } = useContext(UNSAFE_NavigationContext);
    // WHY: same as useLivePathname — a close/unmount handler that conditions on the query string must read
    // the live URL, or a stale search snapshot makes it clobber a navigation that already moved the URL.
    return useCallback(// eslint-disable-next-line no-restricted-syntax
    ()=>navigator.location.search, [
        navigator
    ]);
}
/**
 * The pathname as a render value, sourced from the navigator's synchronous history (re-rendering via its
 * raw listener), NOT the async v6 `useLocation`.
 *
 * WHY: with `v7_startTransition`, `navigate()` commits the URL to history synchronously but defers the
 * React location update inside a transition. A racing non-transition `setState` can preempt and abandon
 * that transition, leaving the URL ahead of the rendered route (a screen "stuck" on the previous step
 * while the URL already moved on — observed on the client-onboarding Next button). Subscribing straight to
 * the history listener keeps this value in lock-step with the URL regardless of whether the transition
 * commits, so URL-derived UI can never desync.
 */ export function useLiveLocationPathname() {
    const { navigator } = useContext(UNSAFE_NavigationContext);
    // eslint-disable-next-line no-restricted-syntax
    const liveNavigator = navigator;
    const subscribe = useCallback((onChange)=>liveNavigator.listen(onChange), [
        liveNavigator
    ]);
    const getSnapshot = useCallback(()=>liveNavigator.location.pathname, [
        liveNavigator
    ]);
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
